import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { History, FileText, FileSpreadsheet, Search, Filter, ArrowRightLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { usePersistedState } from "@/hooks/usePersistedState";
import { useAuth } from "@/context/AuthContext";

function formatEUR(v) {
  return Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

function formatDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

/**
 * StoricoVersamenti — pagina condivisa (agente + collaboratore).
 * L'agente vede tutti i versamenti dell'agenzia; il collaboratore solo i propri.
 * Ogni riga permette di scaricare PDF e Excel del versamento.
 */
export default function StoricoVersamenti() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = usePersistedState("storicovers:q", "");
  const [dateFrom, setDateFrom] = usePersistedState("storicovers:from", "");
  const [dateTo, setDateTo] = usePersistedState("storicovers:to", "");
  const [destinazione, setDestinazione] = usePersistedState("storicovers:dest", "all");   // all | agenzia | conto_corrente
  const [statoConv, setStatoConv] = usePersistedState("storicovers:stato", "all");         // all | in_attesa | convalidato | rifiutato
  const [userFilter, setUserFilter] = usePersistedState("storicovers:user", "all");       // all | <user_id>
  const filtersActive = !!q || !!dateFrom || !!dateTo || destinazione !== "all" || statoConv !== "all" || userFilter !== "all";
  const resetFilters = () => {
    setQ(""); setDateFrom(""); setDateTo(""); setDestinazione("all"); setStatoConv("all"); setUserFilter("all");
  };

  const isAgente = user?.role === "agente" || user?.role === "consultazione";

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/versamenti");
        setRows(r.data || []);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    return rows.filter((v) => {
      if (dateFrom && v.data_versamento < dateFrom) return false;
      if (dateTo && v.data_versamento > dateTo) return false;
      if (destinazione !== "all" && v.destinazione !== destinazione) return false;
      if (statoConv !== "all" && (v.stato_convalida || "in_attesa") !== statoConv) return false;
      if (userFilter !== "all" && v.user_id !== userFilter) return false;
      if (q) {
        const s = q.toLowerCase();
        const name = `${v.user?.nome || ""} ${v.user?.cognome || ""}`.toLowerCase();
        const noteMatch = (v.note || "").toLowerCase().includes(s);
        const entryMatch = (v.entries_preview || []).some(
          (e) => (e.contraente || "").toLowerCase().includes(s) ||
                 (e.numero_polizza || "").toLowerCase().includes(s)
        );
        if (!name.includes(s) && !noteMatch && !entryMatch) return false;
      }
      return true;
    });
  }, [rows, q, dateFrom, dateTo, destinazione, statoConv, userFilter]);

  // Lista unica dei collaboratori/utenti presenti nei versamenti (solo per agente)
  const usersList = useMemo(() => {
    if (!isAgente) return [];
    const map = new Map();
    for (const v of rows) {
      if (v.user && !map.has(v.user_id)) {
        map.set(v.user_id, v.user);
      }
    }
    return Array.from(map.values()).sort((a, b) => (a.cognome || "").localeCompare(b.cognome || ""));
  }, [rows, isAgente]);

  const totale = useMemo(() => filtered.reduce((s, v) => s + Number(v.totale || 0), 0), [filtered]);

  const download = async (id, kind) => {
    try {
      const r = await api.get(`/versamenti/${id}/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const disp = r.headers["content-disposition"] || "";
      const m = disp.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `versamento.${kind === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const changeDestinazione = async (v) => {
    const target = v.destinazione === "agenzia" ? "conto_corrente" : "agenzia";
    const targetLabel = target === "agenzia" ? "In agenzia" : "Su C/C agenzia";
    if (!window.confirm(
      `Sei sicuro di voler cambiare la destinazione di questo versamento da ` +
      `"${v.destinazione === "agenzia" ? "In agenzia" : "Su C/C agenzia"}" a "${targetLabel}"?\n\n` +
      `Importi e polizze resteranno invariati. Il flusso di cassa verrà aggiornato.`
    )) return;
    try {
      const r = await api.post(`/versamenti/${v.id}/change-destinazione`, { destinazione: target });
      setRows((prev) => prev.map((x) => (x.id === v.id ? { ...x, ...r.data } : x)));
      toast.success("Destinazione aggiornata");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="storico-versamenti-page">
      <header className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
          <History className="w-5 h-5 text-blue-700" />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Storico versamenti</h1>
          <p className="text-sm text-slate-600 mt-1">
            {isAgente
              ? "Elenco di tutti i versamenti creati dai collaboratori e da te. Puoi scaricare PDF ed Excel di ogni versamento."
              : "Elenco dei tuoi versamenti passati. Puoi scaricare PDF ed Excel di ognuno."}
          </p>
        </div>
      </header>

      {/* Filtri */}
      <Card className="p-4 border-slate-200" data-testid="storico-vers-filters">
        <div className={`grid grid-cols-1 gap-3 items-end ${isAgente ? "md:grid-cols-6" : "md:grid-cols-5"}`}>
          <div>
            <label className="text-xs uppercase text-slate-500 font-semibold flex items-center gap-1">
              <Search className="w-3 h-3" /> Cerca
            </label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="cliente, polizza, note..." data-testid="vf-search" />
          </div>
          {isAgente && (
            <div>
              <label className="text-xs uppercase text-slate-500 font-semibold">Utente</label>
              <select
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                data-testid="vf-user"
              >
                <option value="all">Tutti gli utenti</option>
                {usersList.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nome} {u.cognome} {u.role === "agente" ? "(agente)" : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs uppercase text-slate-500 font-semibold">Dal</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="vf-from" />
          </div>
          <div>
            <label className="text-xs uppercase text-slate-500 font-semibold">Al</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="vf-to" />
          </div>
          <div>
            <label className="text-xs uppercase text-slate-500 font-semibold flex items-center gap-1">
              <Filter className="w-3 h-3" /> Destinazione
            </label>
            <select
              value={destinazione}
              onChange={(e) => setDestinazione(e.target.value)}
              className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
              data-testid="vf-destinazione"
            >
              <option value="all">Tutte</option>
              <option value="agenzia">In agenzia</option>
              <option value="conto_corrente">Su C/C agenzia</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase text-slate-500 font-semibold">Stato convalida</label>
            <select
              value={statoConv}
              onChange={(e) => setStatoConv(e.target.value)}
              className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
              data-testid="vf-stato"
            >
              <option value="all">Tutti</option>
              <option value="in_attesa">In attesa</option>
              <option value="convalidato">Convalidati</option>
              <option value="rifiutato">Rifiutati</option>
            </select>
          </div>
        </div>
        <div className="mt-3 text-sm text-slate-600 flex justify-between items-center">
          <span className="flex items-center gap-3">
            <span data-testid="vf-count">{filtered.length} versamenti trovati</span>
            {filtersActive && (
              <Button size="sm" variant="outline" onClick={resetFilters} data-testid="vf-reset-filters">Azzera filtri</Button>
            )}
          </span>
          <span className="font-heading font-bold text-slate-900 font-mono-num" data-testid="vf-totale">
            Totale: {formatEUR(totale)}
          </span>
        </div>
      </Card>

      {/* Tabella */}
      <Card className="p-0 border-slate-200 overflow-hidden" data-testid="storico-vers-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Data</th>
                {isAgente && <th className="px-4 py-3 text-left">Collaboratore</th>}
                <th className="px-4 py-3 text-left">Destinazione</th>
                <th className="px-4 py-3 text-center">Polizze</th>
                <th className="px-4 py-3 text-right">Totale</th>
                <th className="px-4 py-3 text-center">Stato</th>
                <th className="px-4 py-3 text-right">Scarica</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={isAgente ? 7 : 6} className="text-center py-10 text-slate-500" data-testid="vf-loading">Caricamento…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={isAgente ? 7 : 6} className="text-center py-10 text-slate-500" data-testid="vf-empty">Nessun versamento trovato.</td></tr>
              )}
              {!loading && filtered.map((v) => {
                const stato = v.stato_convalida || "in_attesa";
                const badge = stato === "convalidato"
                  ? "bg-emerald-100 text-emerald-800"
                  : stato === "rifiutato" ? "bg-red-100 text-red-800"
                  : "bg-amber-100 text-amber-800";
                const destLabel = v.destinazione === "conto_corrente" ? "Su C/C agenzia" : "In agenzia";
                return (
                  <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`vf-row-${v.id}`}>
                    <td className="px-4 py-3 font-mono-num">{formatDate(v.data_versamento)}</td>
                    {isAgente && (
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">
                          {v.user?.nome} {v.user?.cognome}
                        </div>
                        <div className="text-xs text-slate-500">{v.user?.role || ""}</div>
                      </td>
                    )}
                    <td className="px-4 py-3 text-slate-700">
                      <div className="flex items-center gap-2">
                        <span>{destLabel}</span>
                        {v.importo_libero && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">manuale</span>}
                        {isAgente && !v.is_cc_separato_step && (
                          <button
                            type="button"
                            onClick={() => changeDestinazione(v)}
                            className="text-xs text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
                            data-testid={`vf-change-dest-${v.id}`}
                            title="Cambia destinazione (solo la destinazione, importi invariati)"
                          >
                            <ArrowRightLeft className="w-3 h-3" /> Cambia
                          </button>
                        )}
                      </div>
                      {v.destinazione_changed_from && (
                        <div className="text-[10px] text-slate-400 mt-0.5">
                          (era: {v.destinazione_changed_from === "agenzia" ? "in agenzia" : "su C/C"})
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-600">{v.n_polizze || 0}</td>
                    <td className="px-4 py-3 text-right font-mono-num font-semibold">{formatEUR(v.totale)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block text-[11px] px-2 py-0.5 rounded font-semibold ${badge}`}>
                        {stato.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => download(v.id, "pdf")}
                          data-testid={`vf-pdf-${v.id}`}
                          title="Scarica PDF"
                        >
                          <FileText className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => download(v.id, "excel")}
                          data-testid={`vf-xlsx-${v.id}`}
                          title="Scarica Excel"
                        >
                          <FileSpreadsheet className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
